// ActSurvey.tsx — the end-of-Act wellbeing survey (6 dimensions, 1–5).
//
// Shown once at an Act boundary, after the ActBoundary processing screen, and only
// when the user has consented to research. Always skippable. Scores are 1 = "No
// change" → 5 = "Significant change", matching the server's act_survey dimensions.
import React from 'react';
import { Btn, Display, Body } from '@/components/ui';

// Keys MUST match server DIMS (routes/data.js): confidence, selfWorth, control,
// mentalHealth, understandingNeeds, calmEmotions.
const DIMS: { key: string; label: string }[] = [
  { key: 'confidence', label: 'Confidence in myself' },
  { key: 'selfWorth', label: 'My sense of self-worth' },
  { key: 'control', label: 'Feeling more in control of my life' },
  { key: 'mentalHealth', label: 'My mental health' },
  { key: 'understandingNeeds', label: 'Understanding my own needs' },
  { key: 'calmEmotions', label: 'Being able to calm my emotions' },
];

export function ActSurvey({
  actId,
  onSubmit,
  onSkip,
}: {
  actId: string;
  onSubmit: (scores: Record<string, number>) => void;
  onSkip: () => void;
}) {
  const [scores, setScores] = React.useState<Record<string, number>>({});
  const actNo = /act(\d)/.exec(actId)?.[1] ?? '';
  const answered = Object.keys(scores).length;

  return (
    <div style={overlay}>
      <div style={{ position: 'relative', padding: 'calc(var(--safe-top) + 36px) 24px calc(28px + var(--safe-bottom))', maxWidth: 480, margin: '0 auto' }}>
        <div style={eyebrow}>{actNo ? `Reflecting on ACT ${actNo}` : 'A short reflection'}</div>
        <Display parts={{ pre: 'Since you began, how much has ', em: 'changed', post: '?' }} size={25} lh={1.2} />
        <Body size={14} style={{ marginTop: 14, lineHeight: 1.6, color: 'var(--ink-soft)' }}>
          There are no right answers and this is anonymous. If a question doesn’t fit, just leave it.
          <br />
          <span style={{ fontSize: 12.5 }}>1 = no change &nbsp;·&nbsp; 5 = significant change</span>
        </Body>

        <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 18 }}>
          {DIMS.map((d) => (
            <div key={d.key}>
              <Body size={14.5} style={{ color: 'var(--ink)', marginBottom: 9 }}>
                {d.label}
              </Body>
              <div style={{ display: 'flex', gap: 7 }}>
                {[1, 2, 3, 4, 5].map((n) => {
                  const on = scores[d.key] === n;
                  return (
                    <button
                      key={n}
                      onClick={() => setScores((s) => ({ ...s, [d.key]: n }))}
                      aria-label={`${d.label}: ${n}`}
                      style={{
                        flex: 1,
                        height: 44,
                        borderRadius: 12,
                        cursor: 'pointer',
                        fontSize: 15,
                        fontWeight: 600,
                        background: on ? 'linear-gradient(180deg, #27B98C, var(--green))' : 'var(--paper)',
                        color: on ? '#fff' : 'var(--ink-soft)',
                        border: `1px solid ${on ? 'transparent' : 'var(--hair)'}`,
                        boxShadow: on ? '0 6px 16px -8px rgba(22,122,90,0.6)' : 'none',
                        transition: 'all .14s',
                      }}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 26, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Btn full size="lg" onClick={() => onSubmit(scores)} disabled={!answered} style={{ opacity: answered ? 1 : 0.5 }}>
            {answered ? 'Submit' : 'Choose at least one'}
          </Btn>
          <Btn full variant="ghost" onClick={onSkip}>
            Skip this time
          </Btn>
        </div>
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 92,
  background: 'var(--cream)',
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
  animation: 'rise .5s cubic-bezier(.2,.7,.3,1)',
};
const eyebrow: React.CSSProperties = {
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: 'var(--green-deep)',
  marginBottom: 12,
};
